/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useUpdateDomainContactsMutation } from 'api/domain'
import { Address } from 'models/account'
import { DomainContactType } from 'models/domain'
import { getStatesByCountryCode, regex, removePropertyByKeys } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    type: DomainContactType
    form: RefObject<FormikProps<Address>>,
    contact: Address,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function DomainContactForm({ type, form, contact, close }: Props) {
    const id = +location.pathname?.split('/')[2]
    const { data: domainContacts } = domainAPI.endpoints.domainContacts.useQueryState(id)
    const [updateDomainContacts, { isSuccess, isError, reset }] = useUpdateDomainContactsMutation({ fixedCacheKey: 'update-domain-contact' })

    function UpdateState() {
        const { values, initialValues, setFieldValue } = useFormikContext<{ country: string, state: string }>()

        useEffect(() => {
            if (values.country !== initialValues.country && values.state === initialValues.state) {
                setFieldValue('state', getStatesByCountryCode(values.country)[0].state_code)
            }
        }, [values, initialValues, setFieldValue, form.current])

        return null
    }

    useEffect(() => {
        if (isSuccess && form.current) {
            close()
            form.current.setSubmitting(false)
            form.current.resetForm()
            reset()
        }

        if (isError && form.current) {
            form.current.setSubmitting(false)
        }
    }, [isSuccess, isError])

    return (
        <Formik
            innerRef={form}
            initialValues={contact}
            validationSchema={Yup.object({
                firstname: Yup.string()
                    .required('Required'),
                lastname: Yup.string()
                    .required('Required'),
                phone: Yup.string()
                    .required('Required')
                    .matches(regex.internationalPhone, 'Invalid phone number'),
                email: Yup.string()
                    .required('Required')
                    .email('Invalid Email address'),
                address1: Yup.string()
                    .required('Required'),
                suburb: Yup.string()
                    .required('Required'),
                postcode: Yup.number()
                    .typeError('Invalid postcode')
                    .required('Required')
                    .min(1000, 'Invalid postcode')
                    .max(9999, 'Invalid postcode')
            })}
            onSubmit={(values) => {
                const updatedContact = { [type]: values }

                if (domainContacts) {
                    updateDomainContacts({ id, contacts: removePropertyByKeys({ ...domainContacts, ...updatedContact }, ['domain']) })
                }
            }}
        >
            {({ isSubmitting, values }) => (
                <Form id={'updateDomainContactForm'}>
                    <Row>
                        <Field
                            label={'First Name'}
                            name={'firstname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Last Name'}
                            name={'lastname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Field
                        label={'Phone Number'}
                        name={'phone'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Email Address'}
                        name={'email'}
                        type={'text'}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Organisation'}
                        name={'organisation'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Address 1'}
                        name={'address1'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Address 2'}
                        name={'address2'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Row>
                        <Field
                            label={'Suburb'}
                            name={'suburb'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Postcode'}
                            name={'postcode'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Row>
                        <Field
                            label={'State'}
                            name={'state'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={getStatesByCountryCode(values.country).map(({ state_code, state }) => (
                                { label: state, value: state_code }
                            ))}
                            component={NXUIForm.SelectField}
                        />
                        <Field
                            label={'Country'}
                            name={'country'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={[
                                { value: 'AU', label: 'Australia' },
                                { value: 'NZ', label: 'New Zealand' }
                            ]}
                            component={NXUIForm.SelectField}
                        />
                    </Row>
                    <UpdateState/>
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Row = styled.div`
    display: flex;
    justify-content: space-between;

    > div {
        width: 49%;
    }
`
